/**
 * Presentational component for the health-facility searchable select on the Profile page.
 */

import { SearchableSelect } from "@risalabs_frontend_org/oasis-ui-kit";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { HealthFacilityField } from "../../../enums/healthFacilityField";
import type {
  HealthFacility,
  HealthFacilityOption,
  InternalOrganization,
} from "../types";

/** Props for {@link HealthFacilitySelect}. */
interface HealthFacilitySelectProps {
  /** Whether facilities are being fetched from Firestore */
  fetchingHealthFacility: boolean;
  /** Dropdown options filtered by token permissions */
  healthFacilityOptions: HealthFacilityOption[];
  /** Provider's currently assigned facility, if resolved */
  currentHealthFacility: HealthFacility | null;
  /** Internal org shown when the user is on an external facility */
  currentInternalOrganization: InternalOrganization | null;
  /** Whether a facility switch is in progress */
  loading: boolean;
  /** Called when the user selects a different facility */
  onFacilityChange: (facilityId: string) => void;
}

/**
 * Renders the health-facility searchable select with loading states.
 */
export function HealthFacilitySelect({
  fetchingHealthFacility,
  healthFacilityOptions,
  currentHealthFacility,
  currentInternalOrganization,
  loading,
  onFacilityChange,
}: HealthFacilitySelectProps) {
  return (
    <>
      <div className="flex flex-row items-center gap-1">
        {fetchingHealthFacility ? (
          <div className="flex flex-row items-center gap-1">
            <SpinningLoader />
            Fetching healthcare facilities...
          </div>
        ) : (
          <SearchableSelect
            id="health-facility"
            options={healthFacilityOptions.map((healthFacility) => ({
              value: healthFacility.id,
              label: healthFacility.text,
            }))}
            label="Health Facility"
            placeholder={
              currentHealthFacility
                ? (currentHealthFacility[HealthFacilityField.NAME] ??
                  currentHealthFacility.name ??
                  "")
                : currentInternalOrganization
                  ? currentInternalOrganization.facility_name
                  : "Select Health Facility"
            }
            searchPlaceholder="Search facilities"
            onOptionChange={(data) => {
              onFacilityChange(data.value);
            }}
          />
        )}
      </div>
      {loading && (
        <div className="mt-1 flex items-center gap-2">
          <SpinningLoader />
          Changing healthcare facility...
        </div>
      )}
    </>
  );
}
